import type {AppState} from "../../../data/state/AppState";
import type {IModel} from "../../../data/models/Model";
import type {IFilterRow, FilterType} from "../../../data/models/filter/Filter";
import type {ISpotlightRule} from "../../../data/models/View";
import type {SpotlightColorMode} from "../abstract/table/TableRowV5";
import {XyiconFeature} from "../../../generated/api/base";
import {filterModels, findOrGroups} from "../../../data/models/filter/Filter";
import {LogicalSeparator} from "../../../data/models/filter/LogicalSeparator";
import {getFieldSpotlightColor} from "../../../data/models/field/FieldSpotlightUtils";
import {FilterOperatorsV5} from "../filter/operator/FilterOperatorsV5";

/**
 * Evaluates spotlight rules for items and provides methods to get colors and field references.
 * Encapsulates the cache for matched rules to optimize performance.
 */
export class SpotlightRuleEvaluator<T extends IModel> {
	private _matchedRulesCache = new Map<
		string,
		{rowRule?: ISpotlightRule; fieldRule?: ISpotlightRule; fieldBasedRules?: Map<string, ISpotlightRule>}
	>();

	/**
	 * Gets spotlight rules for an item based on the feature and view context.
	 */
	private getSpotlightRules(item: T, appState: AppState, feature: XyiconFeature, isSideGrid: boolean): ISpotlightRule[] {
		try {
			const viewFeature = isSideGrid ? XyiconFeature.SpaceEditor : feature;
			const selectedView = appState?.actions.getSelectedView(viewFeature);
			const spotlightRules = selectedView?.additionalDetails?.spotlightRules;

			if (!spotlightRules) {
				return [];
			}

			if (isSideGrid) {
				const itemFeature = (item as any).ownFeature;
				return itemFeature === XyiconFeature.Xyicon ? spotlightRules.xyiconSpotlightRules || [] : spotlightRules.boundarySpotlightRules || [];
			}

			return spotlightRules.listViewSpotlightRules || [];
		} catch (error) {
			console.warn("Error getting spotlight rules:", error);
			return [];
		}
	}

	/**
	 * Helper function to extract fieldRefID from a rule (works for both conditional and field-based rules).
	 */
	private getRuleFieldRefID(rule: ISpotlightRule): string | null {
		if (rule.ruleType === "field" && rule.fieldRefID) {
			return rule.fieldRefID;
		}
		if (rule.ruleType === "conditional" && rule.filters?.length > 0) {
			const firstFilter = rule.filters.find((f) => f.type === "filter");
			return firstFilter?.type === "filter" ? firstFilter.value.field || null : null;
		}
		return null;
	}

	/**
	 * Get all fieldRefIDs from a conditional rule (for OR conditions with multiple fields).
	 */
	private getAllRuleFieldRefIDs(rule: ISpotlightRule): string[] {
		if (rule.ruleType === "field" && rule.fieldRefID) {
			return [rule.fieldRefID];
		}
		if (rule.ruleType === "conditional" && rule.filters?.length > 0) {
			const fieldRefIDs = new Set<string>();
			for (const filter of rule.filters) {
				if (filter.type === "filter" && filter.value.field) {
					fieldRefIDs.add(filter.value.field);
				}
			}
			return Array.from(fieldRefIDs);
		}
		return [];
	}

	/**
	 * Evaluates spotlight rules for an item and returns the row color.
	 * This is the main entry point for evaluating spotlight rules.
	 */
	evaluateSpotlightColor(item: T, appState: AppState, feature: XyiconFeature, isSideGrid: boolean): string | null {
		try {
			const rules = this.getSpotlightRules(item, appState, feature, isSideGrid);

			if (rules.length === 0) {
				this._matchedRulesCache.delete(item.id);
				return null;
			}

			let rowRule: ISpotlightRule | null = null;
			let fieldRule: ISpotlightRule | null = null;
			// Map to track which fields already have colors (fieldRefID -> rule)
			// This ensures priority: if a field already has a color from a higher priority rule, lower priority rules won't override it
			const fieldBasedRules = new Map<string, ISpotlightRule>();

			for (const rule of rules) {
				const highlightType = rule.highlightType || "row";
				const ruleFieldRefID = this.getRuleFieldRefID(rule);
				const allRuleFieldRefIDs = this.getAllRuleFieldRefIDs(rule);

				// Priority logic: first matching rule wins for each type (row/field)
				// Row and field rules are independent - both can apply
				// For field rules with OR conditions, check if ANY of the fields already have a color
				const anyFieldAlreadyHasColor = allRuleFieldRefIDs.some((fieldRefID) => fieldBasedRules.has(fieldRefID));

				// Priority logic: first matching rule wins for each type (row/field)
				// Row and field rules are independent - both can apply
				// Field-based rules always have highlightType="row", so they follow the row rule logic
				const shouldSkip =
					highlightType === "row"
						? !!rowRule // For row rules, skip if any row rule already exists (priority: first wins)
						: highlightType === "field" && anyFieldAlreadyHasColor; // For field rules, skip if ANY of the fields already have a color (priority)

				if (shouldSkip) {
					continue;
				}

				if (rule.ruleType === "conditional" && rule.filters?.length > 0) {
					const hasOROperator = rule.filters.some((filter) => filter.type === "separator" && filter.value === LogicalSeparator.OR);

					if (highlightType === "row") {
						// For row highlighting, check if rule matches overall
						const filtersMatch = this.evaluateRuleFilters(item, rule.filters, appState);
						if (filtersMatch) {
							if (!rowRule) {
								rowRule = rule;
							}
						}
					} else {
						// For field highlighting:
						// - Pure AND: If all conditions match → highlight all fields
						// - Pure OR: Evaluate each condition individually → highlight only matching fields
						// - Mixed AND/OR: Group into OR groups (AND has precedence), for each matching OR group → highlight all fields from that group
						const matchingFieldRefIDs = this.getMatchingFieldRefIDsForHighlighting(item, rule, appState);

						// Add matching fields to fieldBasedRules map
						for (const fieldRefID of matchingFieldRefIDs) {
							if (!fieldBasedRules.has(fieldRefID)) {
								fieldBasedRules.set(fieldRefID, rule);
							}
						}

						// Only set fieldRule for pure AND conditions (not OR or mixed AND/OR)
						// For pure AND conditions, fieldRule is needed for backward compatibility
						// For OR and mixed AND/OR conditions, we rely solely on fieldBasedRules map
						// because different fields might match from different OR groups
						if (matchingFieldRefIDs.length > 0 && !hasOROperator && !fieldRule) {
							fieldRule = rule;
						}
					}
				} else if (rule.ruleType === "field" && rule.fieldRefID) {
					const fieldColor = this.evaluateFieldBasedRule(item, rule, appState);
					const filtersMatch = rule.filters?.length > 0 ? this.evaluateRuleFilters(item, rule.filters, appState) : true;

					if (fieldColor && filtersMatch) {
						// Only add to fieldBasedRules if highlightType is "field" (not "row")
						// Field-based rules with highlightType="row" should only color the row, not the field
						if (highlightType === "field") {
							fieldBasedRules.set(rule.fieldRefID, rule);
						}

						if (highlightType === "row") {
							if (!rowRule) {
								rowRule = rule;
							}
						} else {
							if (!fieldRule) {
								fieldRule = rule;
							}
						}
					}
				}

				// Don't break early - we need to process all rules to find all matching field rules
				// Each field can have its own color from different rules
			}

			if (rowRule || fieldRule || fieldBasedRules.size > 0) {
				this._matchedRulesCache.set(item.id, {
					rowRule,
					fieldRule,
					fieldBasedRules: fieldBasedRules.size > 0 ? fieldBasedRules : undefined,
				});
			} else {
				this._matchedRulesCache.delete(item.id);
			}

			if (rowRule?.ruleType === "field") {
				return this.evaluateFieldBasedRule(item, rowRule, appState);
			}
			return rowRule?.color || null;
		} catch (error) {
			console.warn("Error evaluating spotlight color:", error);
			return null;
		}
	}

	/**
	 * Gets the fieldRefID that should be highlighted for an item.
	 */
	getSpotlightFieldRefID(item: T, fieldRefID?: string): string | null {
		const cached = this._matchedRulesCache.get(item.id);

		if (!cached) {
			return null;
		}

		// If a specific fieldRefID is provided, check if it has a rule in fieldBasedRules map
		if (fieldRefID && cached.fieldBasedRules?.has(fieldRefID)) {
			return fieldRefID;
		}

		// If fieldBasedRules exists, prioritize it (especially for OR conditions)
		// This ensures OR conditions only return fields that are actually in fieldBasedRules
		if (cached.fieldBasedRules && cached.fieldBasedRules.size > 0) {
			if (!fieldRefID) {
				// No specific field requested, return first field from fieldBasedRules
				return cached.fieldBasedRules.keys().next().value;
			}
			// Specific field requested but not in fieldBasedRules (for OR conditions)
			// Return null to indicate this field shouldn't be colored
			return null;
		}

		// Fallback to fieldRule for backward compatibility (AND conditions and field-based rules)
		const fieldRule = cached.fieldRule;
		if (fieldRule) {
			const ruleFieldRefID = this.getRuleFieldRefID(fieldRule);
			if (ruleFieldRefID) {
				if (!fieldRefID || ruleFieldRefID === fieldRefID) {
					return ruleFieldRefID;
				}
			}
		}

		// Check if rowRule is a field-based rule with highlightType="field" (not "row")
		// Field-based rules with highlightType="row" should only color the row, not the field
		// Default behavior is always field based rules should color the rows
		const rowRule = cached.rowRule;
		if (rowRule?.ruleType === "field" && rowRule.fieldRefID && rowRule.highlightType === "field") {
			if (!fieldRefID || rowRule.fieldRefID === fieldRefID) {
				return rowRule.fieldRefID;
			}
		}

		return null;
	}

	/**
	 * Gets the color application mode for an item (row, field, or both).
	 */
	getColorApplicationMode(item: T): SpotlightColorMode {
		const cached = this._matchedRulesCache.get(item.id);

		const hasRowRule = !!cached?.rowRule;
		const hasFieldRule = !!cached?.fieldRule;
		const hasFieldBasedRules = cached?.fieldBasedRules && cached.fieldBasedRules.size > 0;

		// For OR conditions, we rely on fieldBasedRules instead of fieldRule
		const hasFieldHighlighting = hasFieldRule || hasFieldBasedRules;

		return hasRowRule && hasFieldHighlighting ? "both" : hasFieldHighlighting ? "field" : "row";
	}

	/**
	 * Gets the spotlight color for a specific field in an item.
	 */
	getSpotlightFieldColor(item: T, appState: AppState, fieldRefID?: string): string | null {
		const cached = this._matchedRulesCache.get(item.id);

		if (!cached) {
			return null;
		}

		// If a specific fieldRefID is provided, check if it has a rule in fieldBasedRules map
		if (fieldRefID && cached.fieldBasedRules?.has(fieldRefID)) {
			const rule = cached.fieldBasedRules.get(fieldRefID)!;
			return rule.ruleType === "conditional" ? rule.color : this.evaluateFieldBasedRule(item, rule, appState);
		}

		// If fieldBasedRules exists, prioritize it (especially for OR conditions)
		// This ensures OR conditions only return colors for fields that are actually in fieldBasedRules
		if (cached.fieldBasedRules && cached.fieldBasedRules.size > 0) {
			if (!fieldRefID) {
				// No specific field requested, return first field's color from fieldBasedRules
				const firstRule = cached.fieldBasedRules.values().next().value;
				if (firstRule) {
					return firstRule.ruleType === "conditional" ? firstRule.color : this.evaluateFieldBasedRule(item, firstRule, appState);
				}
			}
			// Specific field requested but not in fieldBasedRules (for OR conditions)
			// Return null to indicate this field shouldn't be colored
			return null;
		}

		// Fallback to fieldRule for backward compatibility (AND conditions and field-based rules)
		const fieldRule = cached.fieldRule;
		if (fieldRule) {
			const ruleFieldRefID = this.getRuleFieldRefID(fieldRule);
			// If no specific field requested, or if the requested field matches, return its color
			if (!fieldRefID || ruleFieldRefID === fieldRefID) {
				return fieldRule.ruleType === "conditional" ? fieldRule.color : this.evaluateFieldBasedRule(item, fieldRule, appState);
			}
		}

		// Check if rowRule is a field-based rule with highlightType="field" (not "row")
		// Field-based rules with highlightType="row" should only color the row, not the field
		// Default behavior is always field based rules should color the rows
		const rowRule = cached.rowRule;
		if (rowRule?.ruleType === "field" && rowRule.fieldRefID && rowRule.highlightType === "field") {
			// If no specific field requested, or if the requested field matches, return its color
			if (!fieldRefID || rowRule.fieldRefID === fieldRefID) {
				return this.evaluateFieldBasedRule(item, rowRule, appState);
			}
		}

		return null;
	}

	/**
	 * Evaluates a single filter condition to check if it matches the item.
	 */
	private evaluateSingleFilter(item: T, filter: IFilterRow, appState: AppState): boolean {
		if (filter.type !== "filter") {
			return false;
		}

		const f = filter.value;
		if (!f.field || !f.operator) {
			return false;
		}

		const operatorConfig = FilterOperatorsV5.map[f.operator];
		if (!operatorConfig?.method) {
			return false;
		}

		const field = appState.actions.getFieldByRefId(f.field);
		if (!field) {
			// Field has been removed -> consider this filter to pass (consistent with matchFilter logic)
			return true;
		}

		// Get the field values from the item
		const values = appState.actions.filterActions.getFilterValues(item, f.field);

		// Match the original matchFilter logic from Filter.ts
		// values can be "" too if eg. fieldData[f35] is not defined yet
		// (Note: we could return empty array from getFilterValues)
		if (values?.some) {
			// If values is an empty array check blank match separately
			if (values.length === 0) {
				return operatorConfig.method("", f.param, field);
			} else {
				// Result is true if at least one of the values matches the method
				return values.some((v: any) => operatorConfig.method(v, f.param, field));
			}
		}

		return false;
	}

	/**
	 * Gets the fieldRefIDs that should be highlighted for field-based rules.
	 * - For pure AND conditions: Returns all fieldRefIDs if the rule matches (all conditions must match).
	 * - For mixed AND/OR conditions: Groups filters into OR groups (AND has precedence).
	 *   For each OR group that matches (all AND filters in that group match), returns all fields from that group.
	 *   If multiple OR groups match, returns all fields from all matching groups.
	 */
	private getMatchingFieldRefIDsForHighlighting(item: T, rule: ISpotlightRule, appState: AppState): string[] {
		if (rule.ruleType !== "conditional" || !rule.filters?.length) {
			return [];
		}

		const allFieldRefIDs = this.getAllRuleFieldRefIDs(rule);
		if (allFieldRefIDs.length === 0) {
			return [];
		}

		// Check if the rule uses OR operators
		const hasOROperator = rule.filters.some((filter) => filter.type === "separator" && filter.value === LogicalSeparator.OR);

		if (!hasOROperator) {
			// Pure AND condition: Check if ALL conditions match, if yes → return all fields
			const filtersMatch = this.evaluateRuleFilters(item, rule.filters, appState);
			return filtersMatch ? allFieldRefIDs : [];
		}

		// Mixed AND/OR condition: Group filters into OR groups (AND has precedence)
		// Each OR group contains filters that are ANDed together
		// For each OR group that matches (all AND filters in that group match), highlight all fields from that group
		const matchingFieldRefIDs: string[] = [];
		const orGroups = findOrGroups(rule.filters);

		// Process each OR group
		for (const orGroup of orGroups) {
			// Check if ALL filters in this OR group match (AND logic within the group)
			let allFiltersInGroupMatch = true;
			const groupFieldRefIDs: string[] = [];

			for (const filter of orGroup) {
				// findOrGroups only returns filters (no separators), so filter is always IFilterRowFilter
				if (filter.value.field) {
					const fieldRefID = filter.value.field;
					groupFieldRefIDs.push(fieldRefID);

					// Check if this individual filter matches
					// Convert IFilterRowFilter to IFilterRow for evaluateSingleFilter
					const filterRow: IFilterRow = {type: "filter", value: filter.value};
					const singleFilterMatch = this.evaluateSingleFilter(item, filterRow, appState);

					if (!singleFilterMatch) {
						allFiltersInGroupMatch = false;
					}
				}
			}

			// If ALL filters in this OR group match, add all fields from this group
			if (allFiltersInGroupMatch && groupFieldRefIDs.length > 0) {
				for (const fieldRefID of groupFieldRefIDs) {
					if (!matchingFieldRefIDs.includes(fieldRefID)) {
						matchingFieldRefIDs.push(fieldRefID);
					}
				}
			}
		}

		return matchingFieldRefIDs;
	}

	/**
	 * Evaluates if an item matches the filter conditions of a rule.
	 */
	private evaluateRuleFilters(item: T, filters: IFilterRow[], appState: AppState): boolean {
		try {
			const hasIncompleteFilters = filters.some((filter) => {
				if (filter.type === "filter") {
					if (!filter.value.field || !filter.value.operator) {
						return true;
					}
					const operatorConfig = FilterOperatorsV5.map[filter.value.operator];
					return operatorConfig?.validator && !operatorConfig.validator(filter.value.param);
				}
				return false;
			});

			if (hasIncompleteFilters) {
				return false;
			}

			const mockFilterState = {
				type: "advanced" as FilterType,
				filters: {simple: [] as IFilterRow[], advanced: filters},
			};
			return filterModels([item], mockFilterState, appState).length > 0;
		} catch (error) {
			console.warn("Error evaluating spotlight rule filters:", error);
			return false;
		}
	}

	/**
	 * Evaluates a field-based rule and returns its color.
	 */
	private evaluateFieldBasedRule(item: T, rule: ISpotlightRule, appState: AppState): string | null {
		try {
			return rule.fieldRefID ? getFieldSpotlightColor(item, rule.fieldRefID, appState) : null;
		} catch (error) {
			console.warn("Error getting field spotlight color:", error);
			return null;
		}
	}
}
